import { FormattedMessage } from 'react-intl'
import { ToastNotification } from '../components'
import { isAppsumoUser, showAppsumoBillingModal } from './appsumoBilling'

// Plan-driven usage limits (contact cap, broadcast window). These read the
// entitlement fields exposed by the plans endpoint; the fields are only set
// for plans that enforce limits (currently AppSumo tiers), so every helper is
// a no-op for unlimited plans. Channel-specific billing UX (where to send the
// user to upgrade) lives in appsumoBilling.jsx.
//
// Semantics:
// - Alerts / send blocks / campaign pauses → strictly OVER the limit (used > limit)
// - Blocking new contacts / imports → at capacity (remaining === 0)

export const hasContactLimit = (entitlements = window?.entitlements) => {
  return (entitlements?.contactLimit ?? entitlements?.contact_limit) != null
}

export const getContactLimitStats = (entitlements = window?.entitlements) => {
  if (!hasContactLimit(entitlements)) {
    return { limit: null, used: null, remaining: null }
  }
  const limit = entitlements?.contactLimit ?? entitlements?.contact_limit
  const used = entitlements?.contactUsed ?? entitlements?.contact_used
  const remaining = entitlements?.contactRemaining ?? entitlements?.contact_remaining
  return {
    limit: limit == null ? null : Number(limit),
    used: used == null ? null : Number(used),
    remaining: remaining == null ? null : Number(remaining),
  }
}

/** True only when used > limit. Exact capacity (e.g. 1000/1000) is NOT over. */
export const isOverContactLimit = (entitlements = window?.entitlements) => {
  if (!hasContactLimit(entitlements)) return false
  if (entitlements?.overContactLimit != null || entitlements?.over_contact_limit != null) {
    return !!(entitlements?.overContactLimit || entitlements?.over_contact_limit)
  }
  // Fallback for older payloads still using at_or_over (now also strictly-over).
  if (entitlements?.atOrOverContactLimit || entitlements?.at_or_over_contact_limit) {
    return true
  }
  const { used, limit } = getContactLimitStats(entitlements)
  if (used == null || limit == null) return false
  return used > limit
}

/** @deprecated Use isOverContactLimit — same strictly-over semantics. */
export const isAtOrOverContactLimit = isOverContactLimit

/** True when the plan still has room for at least one new contact. */
export const canAddContacts = (entitlements = window?.entitlements) => {
  if (!hasContactLimit(entitlements)) return true
  const { remaining } = getContactLimitStats(entitlements)
  return remaining == null || remaining > 0
}

export const getNextBroadcastUnlockAt = (entitlements = window?.entitlements) => {
  return entitlements?.nextBroadcastUnlockAt || entitlements?.next_broadcast_unlock_at || null
}

/** True when the plan caps concurrent autoresponder + drip + automation to N. */
export const hasOngoingCampaignLimit = (entitlements = window?.entitlements) => {
  const max = entitlements?.maxActiveOngoingCampaigns ?? entitlements?.max_active_ongoing_campaigns
  return max != null && Number(max) > 0
}

export const getMaxActiveOngoingCampaigns = (entitlements = window?.entitlements) => {
  if (!hasOngoingCampaignLimit(entitlements)) return null
  return Number(entitlements?.maxActiveOngoingCampaigns ?? entitlements?.max_active_ongoing_campaigns)
}

/**
 * Toast for one-active-campaign cap (in-builder). Campaigns list uses ErrorModal instead.
 */
export const showOngoingCampaignLimitPrompt = () => {
  ToastNotification({
    id: new Date(),
    icon: 'alert',
    color: 'danger',
    body: (
      <FormattedMessage
        id="plan.ongoing_campaign_limit.blocked"
        defaultMessage="You can only have one active autoresponder, drip, or automation at a time. Deactivate your current campaign before activating another."
      />
    ),
  })
}

/**
 * Toast when contact cap blocks activation inside a campaign builder.
 */
export const showContactLimitActivationToast = (entitlements = window?.entitlements) => {
  const { used, limit } = getContactLimitStats(entitlements)
  ToastNotification({
    id: new Date(),
    icon: 'alert',
    color: 'danger',
    body: (
      <FormattedMessage
        id="plan.contact_limit.activate.blocked"
        defaultMessage="Contact limit exceeded ({used}/{limit}). Campaign activation is paused until you are back under your plan limit."
        values={{ used: used ?? '—', limit: limit ?? '—' }}
      />
    ),
  })
}

/**
 * Returns true (and shows toast or leaves modal to caller) when over contact limit.
 * presentation: 'toast' | 'blocked' — 'blocked' only returns true so caller can show ErrorModal.
 */
export const shouldBlockActivateOverContactLimit = (entitlements = window?.entitlements) => {
  return isOverContactLimit(entitlements)
}

/**
 * Route the user to the right upgrade flow for their billing channel:
 * AppSumo users get the AppSumo modal, everyone else the pricing chart.
 */
export const showUpgradePrompt = (entitlements = window?.entitlements) => {
  if (isAppsumoUser(entitlements)) {
    showAppsumoBillingModal('upgrade', entitlements)
  } else {
    window.pricing?.show?.()
  }
}

/**
 * Guard for manual contact creation. Returns true (and shows a warning +
 * upgrade prompt) when there is no remaining contact capacity.
 */
export const guardContactCreation = (entitlements = window?.entitlements) => {
  if (canAddContacts(entitlements)) return false

  const { used, limit } = getContactLimitStats(entitlements)
  ToastNotification({
    id: new Date(),
    icon: 'alert',
    color: 'danger',
    body: (
      <FormattedMessage
        id="plan.contact_limit.create.blocked"
        defaultMessage="You've used all {limit} contacts on your plan ({used}/{limit}). Remove contacts or upgrade your plan to add new contacts."
        values={{ used: used ?? '—', limit: limit ?? '—' }}
      />
    ),
  })
  showUpgradePrompt(entitlements)
  return true
}
